import React, { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { FiClock, FiPlus, FiRefreshCw } from 'react-icons/fi';

import { Button } from '../../../ui/button';
import { Heading } from '../../../ui/heading';
import IconWrapper from '../../../ui/IconWrapper';
import { useAuth } from '../../../../contexts/AuthContext';
import { isAdminUser } from '../../../../utils/api';
import { useNotice } from '../../shared/NoticeProvider';
import {
  useCreateScheduledJob,
  useDeleteScheduledJob,
  usePauseScheduledJob,
  useResumeScheduledJob,
  useRunScheduledJobNow,
  useScheduledJobs,
  useStopScheduledJobRun,
  useUpdateScheduledJob,
} from '../../../../hooks/useScheduledJobs';
import type {
  CreateScheduledJobPayload,
  ScheduledJob,
} from '../../../../types/scheduledJobs';
import CreateScheduledJobDialog from './CreateScheduledJobDialog';
import ConfirmDialog from './ConfirmDialog';
import ScheduledJobDetailDrawer from './ScheduledJobDetailDrawer';
import ScheduledJobsList from './ScheduledJobsList';

/**
 * Top-level admin page for Scheduled Agent Jobs.
 *
 * Hosts:
 *   - Header with create button
 *   - Live list of jobs
 *   - Create / edit dialog
 *   - Delete confirmation
 *   - Detail drawer (runs history + output viewer)
 */
const ScheduledJobsPage: React.FC = () => {
  const { user } = useAuth();
  const notice = useNotice();

  const { data, isLoading, isFetching, refetch } = useScheduledJobs();
  const createMutation = useCreateScheduledJob();
  const updateMutation = useUpdateScheduledJob();
  const deleteMutation = useDeleteScheduledJob();
  const pauseMutation = usePauseScheduledJob();
  const resumeMutation = useResumeScheduledJob();
  const runNowMutation = useRunScheduledJobNow();
  const stopMutation = useStopScheduledJobRun();

  const [createOpen, setCreateOpen] = useState(false);
  const [editingJob, setEditingJob] = useState<ScheduledJob | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ScheduledJob | null>(null);
  const [drawerJobId, setDrawerJobId] = useState<string | null>(null);

  const jobs = useMemo(() => data?.items ?? [], [data?.items]);

  if (!isAdminUser(user)) {
    return <Navigate to="/v4/records" replace />;
  }

  const handleCreateOrUpdate = async (payload: CreateScheduledJobPayload) => {
    try {
      if (editingJob) {
        await updateMutation.mutateAsync({ id: editingJob.id, payload });
        notice.show({
          type: 'success',
          message: `Updated "${payload.name}"`,
          autoDismissMs: 4000,
        });
      } else {
        await createMutation.mutateAsync(payload);
        notice.show({
          type: 'success',
          message: `Created "${payload.name}"`,
          autoDismissMs: 4000,
        });
      }
      setCreateOpen(false);
      setEditingJob(null);
    } catch (error) {
      notice.show({
        type: 'error',
        message: editingJob
          ? 'Failed to update scheduled job'
          : 'Failed to create scheduled job',
        details: error instanceof Error ? [error.message] : undefined,
        autoDismissMs: 6000,
      });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      notice.show({
        type: 'success',
        message: `Deleted "${deleteTarget.name}"`,
        autoDismissMs: 4000,
      });
      if (drawerJobId === deleteTarget.id) {
        setDrawerJobId(null);
      }
      setDeleteTarget(null);
    } catch (error) {
      notice.show({
        type: 'error',
        message: 'Failed to delete scheduled job',
        details: error instanceof Error ? [error.message] : undefined,
        autoDismissMs: 6000,
      });
    }
  };

  const handlePause = async (job: ScheduledJob) => {
    try {
      await pauseMutation.mutateAsync(job.id);
      notice.show({
        type: 'success',
        message: `Paused "${job.name}"`,
        autoDismissMs: 3000,
      });
    } catch (error) {
      notice.show({
        type: 'error',
        message: 'Failed to pause job',
        details: error instanceof Error ? [error.message] : undefined,
        autoDismissMs: 6000,
      });
    }
  };

  const handleResume = async (job: ScheduledJob) => {
    try {
      await resumeMutation.mutateAsync(job.id);
      notice.show({
        type: 'success',
        message: `Resumed "${job.name}"`,
        autoDismissMs: 3000,
      });
    } catch (error) {
      notice.show({
        type: 'error',
        message: 'Failed to resume job',
        details: error instanceof Error ? [error.message] : undefined,
        autoDismissMs: 6000,
      });
    }
  };

  const handleRunNow = async (job: ScheduledJob) => {
    try {
      await runNowMutation.mutateAsync(job.id);
      notice.show({
        type: 'success',
        message: `Queued a run for "${job.name}"`,
        autoDismissMs: 3000,
      });
    } catch (error) {
      notice.show({
        type: 'error',
        message: 'Failed to trigger run',
        details: error instanceof Error ? [error.message] : undefined,
        autoDismissMs: 6000,
      });
    }
  };

  const handleStop = async (job: ScheduledJob) => {
    try {
      await stopMutation.mutateAsync(job.id);
      notice.show({
        type: 'success',
        message: `Stop signal sent for "${job.name}"`,
        autoDismissMs: 3000,
      });
    } catch (error) {
      notice.show({
        type: 'error',
        message: 'Failed to stop run',
        details: error instanceof Error ? [error.message] : undefined,
        autoDismissMs: 6000,
      });
    }
  };

  const handleEdit = (job: ScheduledJob) => {
    setEditingJob(job);
    setCreateOpen(true);
  };

  const handleOpenCreate = () => {
    setEditingJob(null);
    setCreateOpen(true);
  };

  const handleRowClick = (job: ScheduledJob) => {
    setDrawerJobId(job.id);
  };

  return (
    <div className="h-full px-4 py-5 md:px-6 md:py-6">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            Admin
          </p>
          <Heading level={1} className="mt-1 flex items-center gap-3">
            <IconWrapper icon={FiClock} className="h-6 w-6 text-primary" />
            Scheduled agent jobs
          </Heading>
          <p className="mt-2 max-w-2xl text-[13px] text-muted-foreground">
            Run a Juno agent on a recurring schedule. Each job uses a
            natural-language prompt — the agent picks skills from the{' '}
            <code>go_intelligence/skills/</code> library at runtime.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void refetch()}
            disabled={isFetching}
          >
            <IconWrapper
              icon={FiRefreshCw}
              className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`}
            />
            Refresh
          </Button>
          <Button type="button" onClick={handleOpenCreate}>
            <IconWrapper icon={FiPlus} className="mr-2 h-4 w-4" />
            New scheduled job
          </Button>
        </div>
      </div>

      <ScheduledJobsList
        jobs={jobs}
        isLoading={isLoading}
        onRowClick={handleRowClick}
        onRunNow={(job) => void handleRunNow(job)}
        onPause={(job) => void handlePause(job)}
        onResume={(job) => void handleResume(job)}
        onStop={(job) => void handleStop(job)}
        onEdit={handleEdit}
        onDelete={(job) => setDeleteTarget(job)}
      />

      <CreateScheduledJobDialog
        open={createOpen}
        onOpenChange={(next) => {
          setCreateOpen(next);
          if (!next) setEditingJob(null);
        }}
        onSubmit={handleCreateOrUpdate}
        job={editingJob}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete scheduled job?"
        description={
          deleteTarget
            ? `"${deleteTarget.name}" and its run history will be permanently removed.`
            : undefined
        }
        confirmLabel="Delete"
        destructive
        isSubmitting={deleteMutation.isPending}
        onConfirm={handleDelete}
        onOpenChange={(next) => {
          if (!next) setDeleteTarget(null);
        }}
      />

      <ScheduledJobDetailDrawer
        jobId={drawerJobId}
        open={Boolean(drawerJobId)}
        onOpenChange={(next) => {
          if (!next) setDrawerJobId(null);
        }}
      />
    </div>
  );
};

export default ScheduledJobsPage;
